import { createContext, useContext, useState } from "react";
import { cn } from "../../lib/utils.js";

/**
 * Dónde van las acciones de la pantalla (exportar, el alta) cuando la tabla
 * vive dentro de un <AdminPage/>: en la FILA DEL TÍTULO, pegadas a la derecha.
 *
 * El valor es el nodo del DOM al que `TableToolbar` manda sus botones por
 * portal. Fuera de un <AdminPage/> el contexto es `null` y la barra los pinta
 * donde siempre, así que una tabla suelta —un panel, un modal— sigue entera.
 */
const AccionesDeLaPagina = createContext(null);

export function useAdminPageActionsSlot() {
  return useContext(AccionesDeLaPagina);
}

/**
 * AdminPage — el shell de una pantalla de /admin. Fuente única del layout.
 *
 * Existía porque no existía: cada sección se inventó el suyo y el panel se
 * leía como cuatro aplicaciones distintas. Medido en el navegador antes de
 * este componente: Flota y KYC con 16 px de padding y fondo elevado; Cobros,
 * Finanzas y Plataforma con CERO padding y sin fondo (el contenido pegado al
 * borde); Revisiones con padding pero sin fondo; Usuarios con fondo oscuro y
 * el padding repartido dentro de cada banda.
 *
 * `AdminLayout` monta el <Outlet/> en un contenedor de scroll sin padding ni
 * fondo, así que el shell le toca a la página. Este componente lo resuelve una
 * vez: fondo elevado, padding que respira en desktop y aprieta en móvil, y el
 * ritmo vertical entre bloques.
 *
 * La página sigue siendo dueña de su scroll interno: el shell es
 * `flex flex-col` con `min-h-0`, de modo que un hijo con `flex-1 min-h-0
 * overflow-auto` (la tabla en modo `fill`) scrollea por dentro y la cabecera
 * sticky funciona.
 *
 * @param {ReactNode} props.children
 * @param {ReactNode} [props.header] — el título de la pantalla (y lo que lo
 *   acompañe: subtítulo, estado de sincronización). Va en una fila propia, y a
 *   su derecha aterrizan las acciones de la tabla. Se pasa por aquí en vez de
 *   ir suelto en `children` porque esa fila es COMPARTIDA: sin un sitio donde
 *   ponerlas, exportar y el alta se apretaban contra la caja de búsqueda y le
 *   comían el ancho —que en un buscador es justo lo que se escribe—.
 * @param {boolean} [props.flush=false] — sin padding propio, para pantallas de
 *   bandas a sangre (cabecera con border-b de borde a borde). Mantiene el fondo
 *   y el ritmo, que es lo que unifica la lectura entre secciones.
 * @param {string} [props.className]
 */
export function AdminPage({
  children,
  header,
  flush = false,
  className,
  ...props
}) {
  // El nodo se guarda en estado (y no en un ref) a propósito: montarlo tiene
  // que provocar un render, o el portal de la barra se quedaría con el `null`
  // del primer paso y las acciones no aparecerían hasta el siguiente cambio.
  const [ranura, setRanura] = useState(null);

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col bg-background-elevated",
        flush ? "gap-0" : "gap-3 p-4 md:gap-4 md:p-6",
        // Un hijo VACÍO no cobra su hueco. Los paneles cerrados —fichas,
        // formularios, confirmaciones— se montan siempre y dejan un `<div/>`
        // de alto cero cada uno; con `gap` eso son 16 px por panel cerrado
        // comidos al final de la página, y la tabla, que es `flex-1`, se
        // quedaba corta justo esa franja (48 px en Dispositivos, que monta
        // tres). `:empty` es dinámico: en cuanto el panel abre, vuelve.
        // Salvo los `fixed`: el scrim del SlidePanel es un div vacío y,
        // escondido, el clic fuera del panel no lo cerraba.
        "[&>*:empty:not(.fixed)]:hidden",
        className,
      )}
      {...props}
    >
      {/* Sin `header` no hay fila del título que compartir, así que tampoco
          ranura: la barra de la tabla pinta sus acciones donde siempre. Es lo
          que hace que la migración de las pantallas pueda ir de a una. */}
      {header && (
        /* `items-start`: el título es más bajo que los botones, y centrarlo
           lo despegaría del borde superior en el que se lee la pantalla.
           `empty:hidden` en la ranura para que una pantalla sin acciones no
           deje una caja invisible empujando el layout. */
        <div className="flex shrink-0 items-start justify-between gap-3">
          {header}
          <div
            ref={setRanura}
            className="ml-auto flex shrink-0 items-center gap-2 empty:hidden"
          />
        </div>
      )}
      <AccionesDeLaPagina.Provider value={ranura}>
        {children}
      </AccionesDeLaPagina.Provider>
    </div>
  );
}
